import {Component, OnDestroy, OnInit} from '@angular/core';
import {UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {Location} from '@angular/common';
import {MustMatch} from '../../_helpers/MustMatch';
import {CountryService} from '../../country/country.service';
import {Country} from '../../country/Country';
import {ActivatedRoute, Router} from '@angular/router';
import {UserService} from '../../_services/user.service';
import {CityService} from '../../_services/city.service';
import {combineLatest} from 'rxjs';
import { ToastrService } from 'ngx-toastr';

@Component({
    selector: 'app-user-form',
    templateUrl: './user-form.component.html',
    styleUrls: ['./user-form.component.css'],
    standalone: false
})
export class UserFormComponent implements OnInit, OnDestroy {

  loading = true;
  userForm: UntypedFormGroup;
  countries: Country[];
  cities: any[] = [];
  submitting = false;
  userToEdit: any;
  userId: any;

  constructor (
    private fb: UntypedFormBuilder,
    public location: Location,
    private toastService: ToastrService,
    private cs: CountryService,
    private userService: UserService,
    private cityService: CityService,
    private activatedRoute: ActivatedRoute,
    private router: Router) {
      this.userId = this.activatedRoute.snapshot.paramMap.get('id');

    this.userForm = this.fb.group({
      id: new UntypedFormControl(0),
      firstName: new UntypedFormControl('', Validators.required),
      lastName: new UntypedFormControl('', Validators.required),
      city: new UntypedFormControl('', Validators.required),
      countryId: new UntypedFormControl(37, Validators.required),
      email: new UntypedFormControl('', Validators.email),
      phone: new UntypedFormControl('', Validators.required),
      password: new UntypedFormControl('', [Validators.required, Validators.minLength(6)]),
      confirmPassword: new UntypedFormControl('')
    }, {
      validator: MustMatch('password', 'confirmPassword')}
    );
  }

  ngOnInit() {
    if (this.userId) {
      combineLatest([
        this.cs.loadAll(),
        this.userService.loadByAdmin(this.userId)
      ]).subscribe(
          ([countries, userToEdit]) => {
            this.countries = countries;
            this.userToEdit = userToEdit;
            this.setFormValues();
          },
          (error) => {
            this.toastService.error(error);
            this.router.navigateByUrl('/admin/users');
          },
          () => {
            this.loading = false;
          }
      );
    } else {
      this.cs.loadAll().subscribe(
          (countries) => {
            this.countries = countries;
            this.cityService.getCitiesByCountry(37).subscribe((response: any) => {
              this.cities = response?.data || response || [];
            });
            this.loading = false;
          },
          (error) => {
            this.toastService.error('Failed to load countries');
            this.router.navigateByUrl('/admin/users');
          }
      );
    }
  }

  setFormValues(){
    if (this.userToEdit !== null ) {
      const userCountryId = this.userToEdit.countryId || (this.userToEdit.country ? this.userToEdit.country.id : 37);
      this.cityService.getCitiesByCountry(userCountryId).subscribe((response: any) => {
        this.cities = response?.data || response || [];
      });
      this.userForm.patchValue({
        id: this.userToEdit.id,
        firstName: this.userToEdit.firstName || '',
        lastName: this.userToEdit.lastName || '',
        email: this.userToEdit.email,
        phone: this.userToEdit.phone,
        countryId: userCountryId,
        city: this.userToEdit.city || this.userToEdit.town || '',
        password: '',
        confirmPassword: ''
      });
      this.userForm.get('password').clearValidators();
      this.userForm.get('confirmPassword').clearValidators();
      this.userForm.get('password').updateValueAndValidity();
      this.userForm.get('confirmPassword').updateValueAndValidity();
    }
  }

  onCountryChange(countryId: number) {
    this.userForm.patchValue({ city: '' });
    this.cityService.getCitiesByCountry(countryId).subscribe((response: any) => {
      this.cities = response?.data || response || [];
    });
  }

  get f() { return this.userForm.controls; }

  back_to_previous() {
    this.location.back();
  }

  submitUserForm() {
    if (!this.userForm.valid) {
      return;
    }
    this.submitting = true;

    const formValue = this.userForm.value;
    const payload: any = {
      firstName: formValue.firstName,
      lastName: formValue.lastName,
      city: formValue.city,
      countryId: parseInt(formValue.countryId, 10),
      email: formValue.email,
      phone: formValue.phone
    };

    if (formValue.password) {
      payload.password = formValue.password;
      payload.confirmPassword = formValue.confirmPassword;
    }

    if (!formValue.id) {
      payload.userType = 'admin';
      payload.code = 237;
      this.userService.addUser(payload).subscribe(
          () => {
            this.router.navigate(['/admin/users']);
          },
          (error) => {
            this.toastService.error(error?.error?.message || 'Failed to create user');
            this.submitting = false;
          }
      );
    } else {
      this.userService.updateUserByAdmin(formValue.id, payload).subscribe(
          () => {
            this.router.navigate(['/admin/users']);
          },
          (error) => {
            this.toastService.error(error?.error?.message || 'Failed to update user');
            this.submitting = false;
          }
      );
    }
  }

  ngOnDestroy(): void {
    window.localStorage.removeItem('user-to-edit');
  }

}
